import { m } from 'motion/react';
import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { viewLocation } from 'components/tables/list-table/filter-bar/builtInViews';
import { PlusIcon } from 'components/icons';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from 'components/ui/context-menu';
import { useReducedMotion } from 'hooks/useReducedMotion';
import { useGetListViewsQuery } from 'store/api/listViews';
import type { ListKey } from 'types/listViews';
import { cn } from 'utils/cn';
import { FAMILY, familyTileClass, LIST_FAMILY } from 'utils/families';
import { t } from 'utils/getTranslations';
import { Pin, pinMark, usePins } from 'utils/pins';
import { FOCUS_RING, FOCUS_RING_INSET } from '../focusRing';
import { rowShift, usePinDragState } from './usePinDrag';

const HEADING_ID = 'menu-pinned-heading';

/**
 * How Pinned marks a pin: its kind's glyph on its family's tile, a plain tile for a kind of no family, or a square
 * in its list's family for a view. A record of a kind this build no longer knows gets the square of a list of none.
 */
export function PinMark({ pin }: { pin: Pin }) {
    const mark = pinMark(pin);
    const listFamily = pin.view && LIST_FAMILY[pin.view.listKey];
    return mark ? (
        <span
            aria-hidden
            className={cn(
                'flex size-[18px] shrink-0 items-center justify-center rounded-[5px]',
                mark.family ? familyTileClass(mark.family) : 'bg-card text-foreground-secondary'
            )}
        >
            <mark.icon className="size-3" />
        </span>
    ) : (
        <span
            aria-hidden
            className={cn(
                'mx-[5px] size-2 shrink-0 rounded-[2px]',
                listFamily ? FAMILY[listFamily].mark : 'bg-muted-foreground'
            )}
        />
    );
}

type LinkProps = { label: string; to: string; pin: Pin; className?: string };

/** A pin's row, carrying its pin for a drag. */
function PinnedLink({ label, to, pin, className }: LinkProps) {
    const { pathname, search } = useLocation();
    const active = `${pathname}${search}` === to;
    return (
        <Link
            to={to}
            title={label}
            data-pin={JSON.stringify(pin)}
            aria-current={active ? 'page' : undefined}
            className={cn(
                // It covers the 1px to the next row, so the hover and a click there do not drop between them.
                'relative flex h-7 w-full items-center gap-2.5 rounded-md px-2.5 after:absolute after:inset-x-0 after:top-full after:h-px pointer-coarse:h-11',
                FOCUS_RING_INSET,
                active
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground font-semibold [&_svg[stroke]]:stroke-2'
                    : 'hover:bg-sidebar-hover',
                className
            )}
        >
            <PinMark pin={pin} />
            <span className="min-w-0 flex-1 truncate">{label}</span>
        </Link>
    );
}

/**
 * A pinned view under its current name, and under the name it was pinned with while its list loads; one that was
 * deleted or is no longer shared drops out of the pins. Only a settled read decides that: a view pinned as it is
 * created mounts while the list is still refetching without it.
 */
function PinnedView({ view: { listKey, id }, ...props }: LinkProps & { view: { listKey: ListKey; id: number } }) {
    const { pin } = props;
    const { setPinned } = usePins();
    const { data, isSuccess, isFetching } = useGetListViewsQuery(listKey);
    const view = data?.views.find((candidate) => candidate.id === id);
    const gone = isSuccess && !isFetching && !view;
    useEffect(() => {
        if (gone) setPinned(pin, false);
    }, [gone, pin, setPinned]);
    if (gone) return null;
    // ponytail: until the list loads the row opens the bare list, which goes to the viewer's default view.
    return <PinnedLink {...props} label={view?.name ?? pin.label} to={view ? viewLocation(pin.url, view) : pin.url} />;
}

/**
 * The sidebar's Pinned section, between My work and Firm, with a hint while nothing is pinned; the rail has none.
 * While a pin is dragged it is the drop zone: dashed, then solid once the magnet docks the chip, the rows making
 * room under the landing row. A pin's context menu moves it or unpins it without dragging.
 */
export default function Pinned() {
    const { pins, placePin, setPinned } = usePins();
    const groupRef = useRef<HTMLDivElement>(null);
    // The unpinned row goes, and its menu with it: focus goes to the row after it, else the one before, else the
    // section itself. The menu puts it there as it closes: moved before that, the open menu would take it back,
    // then return it to the row that is gone. A menu that opens starts with no such target, and closed any other
    // way it returns focus as menus do.
    const afterUnpin = useRef<HTMLElement | null>(null);
    const unpin = (pin: Pin, position: number) => {
        const rows = groupRef.current?.querySelectorAll<HTMLElement>('[data-pin-list] a');
        afterUnpin.current = rows?.[position + 1] ?? rows?.[position - 1] ?? groupRef.current;
        setPinned(pin, false);
    };
    const forgetUnpin = (open: boolean) => {
        if (open) afterUnpin.current = null;
    };
    const focusAfterMenu = () => afterUnpin.current ?? true;
    const { drag, flash } = usePinDragState();
    const still = useReducedMotion();
    const dragging = drag?.phase === 'drag';
    const moving = drag && drag.from >= 0;
    const dropRow = dragging && !moving;

    return (
        <div
            ref={groupRef}
            role="group"
            aria-labelledby={HEADING_ID}
            tabIndex={-1}
            data-tour="sidebar-pinned"
            className={cn('sidebar-fade relative isolate flex flex-col rounded-md', FOCUS_RING)}
        >
            <span
                data-pin-zone
                aria-hidden
                className={cn(
                    'pointer-events-none absolute -inset-x-[7px] -inset-y-1.5 -z-10 rounded-[10px] border-[1.5px] transition-opacity duration-160',
                    drag?.docked
                        ? 'border-secondary bg-changed ring-secondary/30 ring-4'
                        : 'border-changed-border bg-changed/60 border-dashed',
                    !dragging && 'opacity-0'
                )}
            />
            <div id={HEADING_ID} className="text-label text-sidebar-muted px-2.5 pb-1 uppercase">
                {t('menu.pinned')}
            </div>
            {pins.length === 0 && !dropRow ? (
                <p className="text-meta text-sidebar-muted px-2.5 text-pretty whitespace-normal">
                    {t('menu.pinned-empty')}
                </p>
            ) : (
                <ul data-pin-list className="flex flex-col gap-px">
                    {pins.map((pin, position) => {
                        const shift = drag ? rowShift(position, drag) : 0;
                        const row = {
                            label: pin.label,
                            to: pin.url,
                            pin,
                            className: cn(
                                drag?.already && pin.key === drag.pin.key && 'ring-secondary ring-2',
                                flash === pin.key && 'pin-flash'
                            ),
                        };
                        return (
                            <m.li
                                key={pin.key}
                                // A whole `transform`, which Motion hands to the compositor; its `y` runs on the main
                                // thread.
                                animate={{ transform: `translateY(${shift}px)` }}
                                // Rows slide on a spring while the drag runs and back on a cancel; a drop moves them at
                                // once. `MotionConfig` skips `y`, not a whole `transform`: reduced motion is branched here.
                                transition={
                                    (dragging || drag?.phase === 'cancel') && !still
                                        ? { type: 'spring', visualDuration: 0.15, bounce: 0 }
                                        : { duration: 0 }
                                }
                                className={cn(moving && pin.key === drag.pin.key && 'invisible')}
                            >
                                <ContextMenu onOpenChange={forgetUnpin}>
                                    <ContextMenuTrigger className="contents">
                                        {pin.view ? <PinnedView {...row} view={pin.view} /> : <PinnedLink {...row} />}
                                    </ContextMenuTrigger>
                                    <ContextMenuContent finalFocus={focusAfterMenu}>
                                        {position > 0 && (
                                            <ContextMenuItem onClick={() => placePin(pin, position - 1)}>
                                                {t('pins.move-up')}
                                            </ContextMenuItem>
                                        )}
                                        {position < pins.length - 1 && (
                                            <ContextMenuItem onClick={() => placePin(pin, position + 1)}>
                                                {t('pins.move-down')}
                                            </ContextMenuItem>
                                        )}
                                        <ContextMenuItem onClick={() => unpin(pin, position)}>
                                            {t('pins.unpin')}
                                        </ContextMenuItem>
                                    </ContextMenuContent>
                                </ContextMenu>
                            </m.li>
                        );
                    })}
                    {dropRow && (
                        <li
                            className={cn(
                                'border-changed-border text-changed-ink text-meta flex h-7 origin-top items-center gap-2.5 rounded-md border border-dashed px-2.5 transition-[opacity,scale] duration-(--duration-quick) ease-out starting:opacity-0 motion-safe:starting:scale-95 pointer-coarse:h-11',
                                drag.docked && 'opacity-0'
                            )}
                        >
                            <span className="flex w-[18px] justify-center">
                                <PlusIcon className="size-3.5" />
                            </span>
                            {t(drag.already ? 'pins.already-pinned' : 'pins.drop-here')}
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
}
